import { usePathname, useRouter } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import { Spinner } from "@/components/ui/spinner";
import { DASHBOARD, LOGIN } from "@/shared/constants/path";
import { checkIsAuthRoute } from "@/shared/lib/routes";
import useStore from "@/shared/store/useStore";
import usePermissionStore from "@/shared/store/usePermissionStore";
import { AuthAxios } from "@/shared/api/axiosInstance";
import queryClient from "./queryClient";

/**
 * withPrivateRoute
 * Route guard based on:
 * - store auth state
 * - server validation (/auth/me)
 * - permission store hydration (/auth/session-info + persisted features)
 * - middleware cookie guard
 */
export default function withPrivateRoute(WrappedComponent: any) {
  const ComponentRoute = (props: any) => {
    const router = useRouter();
    const pathname = usePathname();

    const { isAuthenticated, isLoading, logout, stopLoading, user } = useStore(
      (state) => state,
    );

    const permissionLoaded = usePermissionStore((s) => s.isLoaded);

    const [validated, setValidated] = useState(false);

    // =====================================================
    // SERVER SESSION VALIDATION + PERMISSION HYDRATION
    // =====================================================
    const validateSession = useCallback(async () => {
      const isAuthRoute = checkIsAuthRoute(pathname);

      // Not authenticated — skip validation
      if (!isAuthenticated) {
        stopLoading();
        setValidated(true);
        return;
      }

      // Always validate session via /auth/me when isAuthenticated is true,
      // even on auth routes (login page). This catches expired access_token
      // and clears stale localStorage to prevent redirect loops.
      try {
        await AuthAxios.get("/auth/me");

        // Session valid — hydrate permission store if not already loaded
        if (!usePermissionStore.getState().isLoaded) {
          try {
            const res = await AuthAxios.get("/auth/session-info");
            const data = res.data?.data;

            // Features from persisted auth store, feature_access from JWT
            const features = user?.features ?? [];
            const featureAccess = data?.feature_access ?? {};

            usePermissionStore.getState().setPermissions({
              features,
              feature_access: featureAccess,
            });
          } catch (err) {
            console.error("[withPrivateRoute] Failed to hydrate permissions:", err);
          }
        }
      } catch {
        logout();
        queryClient.clear();
      } finally {
        stopLoading();
        setValidated(true);
      }
    }, [isAuthenticated, logout, stopLoading, user, pathname]);

    useEffect(() => {
      validateSession();
    }, [validateSession]);

    // =====================================================
    // REDIRECT RULES
    // =====================================================
    useEffect(() => {
      if (isLoading || !validated) return;

      const isAuthRoute = checkIsAuthRoute(pathname);

      if (!isAuthenticated && !isAuthRoute) {
        queryClient.clear();
        router.replace(LOGIN);
        return;
      }

      if (isAuthenticated && isAuthRoute) {
        router.replace(DASHBOARD);
        return;
      }
    }, [isAuthenticated, isLoading, validated, pathname, router]);

    // =====================================================
    // LOADING STATE
    // =====================================================
    if (isLoading || !validated || (isAuthenticated && !permissionLoaded)) {
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <Spinner size="medium" />
        </div>
      );
    }

    return <WrappedComponent {...props} />;
  };

  return ComponentRoute;
}
